import { parse, stringify } from 'flatted';
import {
  clear,
  createStore,
  get,
  getMany,
  set,
  setMany,
  UseStore,
} from 'idb-keyval';
import { pickBy } from 'lodash-es';
import { getFlattenedListings } from 'src/modals/MergeListings/utils';
import {
  ActionOutboxEntityType,
  CatalogResults,
  CatalogResultsSlim,
  Country,
  Event,
  EventEntities,
  EventEntityCounts,
  EventWithData,
  Listing,
  Performer,
  Venue,
} from 'src/WebApiController';

import {
  COUNTRIES_INDEX_DB_KEY,
  EVENT_INDEX_DB_KEY_PREFIX,
  EventIdWithRowVersion,
  ExpandedEventData,
  PERFORMER_INDEX_DB_KEY_PREFIX,
  VENUE_INDEX_DB_KEY_PREFIX,
} from './CatalogDataContext.types';

export const updateCatalogResults = (
  catalogResults: CatalogResults,
  expandedEventsData: ExpandedEventData[]
): CatalogResults => {
  if (!expandedEventsData || !expandedEventsData?.length) {
    return catalogResults;
  }

  const finalCatalogResults: CatalogResults = {
    ...catalogResults,
    events: { ...catalogResults.events },
  };

  for (const expandedEventData of expandedEventsData) {
    for (const eventId in expandedEventData) {
      const curEvent = finalCatalogResults.events[eventId];
      if (curEvent) {
        // Update listings
        const listings = expandedEventData[eventId].listings;
        let listGrpCnt = 0;
        let ungrListCnt = 0;
        listings?.forEach((listing: Listing) => {
          listing.isLtGrp ? listGrpCnt++ : ungrListCnt++;
        });

        const listCnt =
          listGrpCnt > 0
            ? getFlattenedListings(listings)[0].length // if has groups, get flattened listings
            : listings?.length; // else just use the item count

        const sales = expandedEventData[eventId].sales;
        const salesCnt = sales?.length;

        const ticketGroups = expandedEventData[eventId].ticketGroups;
        const tktGrpsCnt = ticketGroups?.length;

        if (listings || sales || ticketGroups) {
          finalCatalogResults.events[eventId] = {
            ...curEvent,
            entities: {
              ...(curEvent.entities ?? {}),
              listings,
              sales,
              ticketGroups,
            } as EventEntities,
            counts: {
              ...(curEvent.counts ?? {}),
              listCnt,
              listGrpCnt,
              ungrListCnt,
              salesCnt,
              tktGrpsCnt,
            } as EventEntityCounts,
          };
        }
      }
    }
  }

  return finalCatalogResults;
};

export const getEventsWithNonZeroCounts = (
  entityType: ActionOutboxEntityType,
  data?: EventWithData[] | null,
  requireEventNotNull?: boolean
) => {
  if (!data) {
    return [];
  }
  const filteredEvents = data.filter((ev) => {
    if (ev.counts == null) {
      return false;
    }

    if (requireEventNotNull && ev.event == null) {
      return false;
    }

    const count =
      entityType === ActionOutboxEntityType.Listing
        ? ev.counts.listCnt
        : entityType === ActionOutboxEntityType.Sale
          ? ev.counts.salesCnt
          : ev.counts.tktGrpsCnt || ev.counts.poCnt;

    return Boolean(count);
  });

  return filteredEvents;
};

export const hasEventsWithFullLoadedData = (
  entityType: ActionOutboxEntityType,
  data?: EventWithData[] | null
) => {
  if (!data) {
    return [];
  }
  const hasEventFullyLoaded = data.some((ev) => {
    if (ev.counts == null) {
      return false;
    }

    if (ev.event == null) {
      return false;
    }

    // The difference between this and the previous getEventsWithNonZeroCounts is that we check if the count is not null instead of checking if it is not 0.
    const count =
      entityType === ActionOutboxEntityType.Listing
        ? ev.counts.listCnt != null
        : entityType === ActionOutboxEntityType.Sale
          ? ev.counts.salesCnt != null
          : ev.counts.tktGrpsCnt != null || ev.counts.poCnt != null;

    return Boolean(count);
  });

  return hasEventFullyLoaded;
};

// We should only override the dataIsLoaded if the new item is loaded
export const mergeListingDataIsLoaded = (
  oldItem: Listing,
  newItem: Listing
) => {
  return {
    ...oldItem.dataIsLoaded,
    ...pickBy(newItem.dataIsLoaded, (value) => value === true),
  };
};

export const createCatalogIdbStore = () =>
  createStore('catalog', 'catalog-store');

export const clearCatalogIdbStore = async () => {
  const idbStore = createCatalogIdbStore();
  clear(idbStore);
};

export const persistCatalogData = (
  idbStore: UseStore,
  data: CatalogResultsSlim
) => {
  try {
    if (data.events) {
      setMany(
        data.events.map((ev) => {
          return [EVENT_INDEX_DB_KEY_PREFIX + ev.virtId, stringify(ev)];
        }),
        idbStore
      );
    }

    if (data.performers) {
      setMany(
        data.performers.map((performer) => {
          return [
            PERFORMER_INDEX_DB_KEY_PREFIX + performer.viagId,
            stringify(performer),
          ];
        }),
        idbStore
      );
    }

    if (data.venues) {
      setMany(
        data.venues.map((venue) => {
          return [VENUE_INDEX_DB_KEY_PREFIX + venue.viagId, stringify(venue)];
        }),
        idbStore
      );
    }

    if (data.countries) {
      set(COUNTRIES_INDEX_DB_KEY, stringify(data.countries), idbStore);
    }
  } catch (e) {
    console.warn('Unable to persist catalog data to idb', e);
  }
};

export const loadCatalogData = async (
  idbStore: UseStore,
  eventIdsNeedingData: EventIdWithRowVersion[]
) => {
  console.debug(
    'Loading catalog data from idb - event counts',
    eventIdsNeedingData.length
  );
  const startDate = new Date().getTime();

  const cachedData = {
    events: [],
    performers: [],
    venues: [],
    countries: [],
  } as CatalogResultsSlim;

  const realEventIdsNeedingData = {} as Record<string, EventIdWithRowVersion>;

  const perfIdsNeedingData: [number, EventIdWithRowVersion][] = [];
  const venueIdsNeedingData: [number, EventIdWithRowVersion][] = [];

  const countriesPromise = get(COUNTRIES_INDEX_DB_KEY, idbStore).then(
    (countries) => {
      if (countries) {
        const parsedCountries = parse(countries) as Country[];
        cachedData.countries = parsedCountries;
      }
    }
  );

  // TODO - POSS-37 - we will need to return PosEvent row-version with Event
  // as well as CatalogCount so that we can check if any event is obsolete - we
  // need to get the latest data from the server

  await getMany(
    eventIdsNeedingData.map((id) => EVENT_INDEX_DB_KEY_PREFIX + id.eventId),
    idbStore
  ).then(
    (events: (string | undefined)[]) => {
      events.forEach((ev, index) => {
        const id = eventIdsNeedingData[index];
        let oldRowVer: number | undefined | null = undefined;
        let found = false;
        if (ev) {
          const parsedEv = parse(ev) as Event;
          if (parsedEv) {
            // Only use the Event in the idb cache if the row version is >= the latest row version
            oldRowVer = parsedEv.rowVer;
            if ((oldRowVer ?? 0) >= (id.rowVer ?? 0)) {
              cachedData.events.push(parsedEv);
              found = true;

              if (parsedEv.perfId) {
                perfIdsNeedingData.push([
                  parsedEv.perfId,
                  eventIdsNeedingData[index],
                ]);
              }
              venueIdsNeedingData.push([
                parsedEv.venId,
                eventIdsNeedingData[index],
              ]);
            }
          }
        }
        if (!found) {
          realEventIdsNeedingData[id.eventId] = { ...id, oldRowVer: oldRowVer };
        }
      });
    },
    (e) => {
      console.warn('Unable to load event data from idb', e);
      eventIdsNeedingData.forEach(
        (id) => (realEventIdsNeedingData[id.eventId] = id)
      );
    }
  );

  const perfPromise = getMany(
    perfIdsNeedingData.map((id) => PERFORMER_INDEX_DB_KEY_PREFIX + id[0]),
    idbStore
  ).then(
    (perfs: (string | undefined)[]) => {
      let found = false;
      perfs.forEach((perf, index) => {
        if (perf) {
          const parsedPerf = parse(perf) as Performer;
          if (parsedPerf) {
            cachedData.performers.push(parsedPerf);
            found = true;
          }
        }
        if (!found) {
          realEventIdsNeedingData[perfIdsNeedingData[index][1].eventId] =
            perfIdsNeedingData[index][1];
        }
      });
    },
    (e) => {
      console.warn('Unable to load performers from idb', e);
      perfIdsNeedingData.forEach((perfId) => {
        const eventId = perfId[1];
        realEventIdsNeedingData[eventId.eventId] = eventId;
      });
    }
  );

  const venuePromise = getMany(
    venueIdsNeedingData.map((id) => VENUE_INDEX_DB_KEY_PREFIX + id[0]),
    idbStore
  ).then(
    (venues: (string | undefined)[]) => {
      let found = false;
      venues.forEach((venue, index) => {
        if (venue) {
          const parsedVenue = parse(venue) as Venue;
          if (parsedVenue) {
            cachedData.venues.push(parsedVenue);
            found = true;
          }
        }
        if (!found) {
          const eventId = venueIdsNeedingData[index][1];
          realEventIdsNeedingData[eventId.eventId] = eventId;
        }
      });
    },
    (e) => {
      console.warn('Unable to load venues from idb', e);
      venueIdsNeedingData.forEach((venueId) => {
        const eventId = venueId[1];
        realEventIdsNeedingData[eventId.eventId] = eventId;
      });
    }
  );

  await Promise.all([countriesPromise, perfPromise, venuePromise]);

  const endDate = new Date().getTime();

  const elapsedTime = endDate - startDate;
  console.debug(
    `Loading catalog data from idb took ${elapsedTime} ms for ${eventIdsNeedingData.length} items`
  );

  const realEventIdsNeedingDataKeys = Object.keys(realEventIdsNeedingData);
  if (realEventIdsNeedingDataKeys.length) {
    console.debug(
      'Need to get catalog data for the event count:',
      realEventIdsNeedingDataKeys.length
    );
  }
  return {
    cachedData,
    realEventIdsNeedingData: realEventIdsNeedingDataKeys,
  };
};
